/**
 * ReportLauncherWidget — Report generation for subject dashboard (DRR-321)
 *
 * Shows available report templates using generated hooks.
 * Uses ListTemplatesByCategory as report templates follow PolicyTemplate pattern.
 */

import React, { useState, useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Button,
  Modal,
  Box,
  ScrollArea,
} from '@mantine/core';
import {
  IconFileDescription,
  IconAlertCircle,
  IconDownload,
  IconPrinter,
  IconFileText,
  IconChevronRight,
} from '@tabler/icons-react';
import {
  useListTemplatesByCategoryQuery,
  useGenerateSubjectCareSummaryMutation,
  PolicyTemplateCategory,
} from '@/generated/graphql';
import type { WidgetProps } from '@ferp/core';

interface ReportTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  clinical: <IconFileDescription size={14} />,
  billing: <IconFileText size={14} />,
  operational: <IconFileDescription size={14} />,
  regulatory: <IconFileDescription size={14} />,
};

const CATEGORY_COLORS: Record<string, string> = {
  clinical: 'blue',
  billing: 'green',
  operational: 'orange',
  regulatory: 'violet',
};

export function ReportLauncherWidget({ subjectActorId }: WidgetProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);

  const { data, loading, error } = useListTemplatesByCategoryQuery({
    variables: { category: PolicyTemplateCategory.REGULATORY_COMPLIANCE },
    fetchPolicy: 'cache-and-network',
  });

  const [generateReport, { loading: generating }] = useGenerateSubjectCareSummaryMutation({
    onCompleted: () => {
      setSelectedTemplate(null);
    },
  });

  const templates = useMemo((): ReportTemplate[] => {
    return (data?.listTemplatesByCategory ?? []).map(t => ({
      id: t.templateId,
      name: t.templateName,
      description: t.description,
      category: t.category,
    }));
  }, [data]);

  const handleGenerate = () => {
    if (!selectedTemplate || !subjectActorId) return;
    generateReport({
      variables: {
        input: {
          subjectActorId,
          tenantId: '', // Will be resolved from auth context
          templateId: selectedTemplate.id,
        },
      },
    }).catch(() => {});
  };

  if (loading && templates.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading templates.
      </Alert>
    );
  }

  if (templates.length === 0) {
    return (
      <Alert icon={<IconFileDescription size={16} />} color="blue" variant="light">
        No report templates available.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <IconFileDescription size={16} color="var(--mantine-color-blue-6)" />
        <Text fw={600} size="sm">Reports</Text>
        <Badge size="sm" variant="light">{templates.length} available</Badge>
      </Group>

      <ScrollArea.Autosize mah={250}>
        <Stack gap="xs">
          {templates.slice(0, 5).map((template) => (
            <Card
              key={template.id}
              withBorder
              padding="xs"
              radius="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedTemplate(template)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  {CATEGORY_ICONS[template.category ?? 'operational'] ?? <IconFileDescription size={14} />}
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={500} size="sm" lineClamp={1}>{template.name}</Text>
                    {template.description && (
                      <Text size="xs" c="dimmed" lineClamp={1}>{template.description}</Text>
                    )}
                  </Box>
                </Group>
                <Group gap={4}>
                  <Badge size="xs" variant="light" color={CATEGORY_COLORS[template.category ?? 'operational'] ?? 'gray'}>
                    PDF
                  </Badge>
                  <IconChevronRight size={14} color="gray" />
                </Group>
              </Group>
            </Card>
          ))}
          {templates.length > 5 && (
            <Text size="xs" c="dimmed" ta="center">
              +{templates.length - 5} more
            </Text>
          )}
        </Stack>
      </ScrollArea.Autosize>

      <Modal
        opened={!!selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        title={`Generate: ${selectedTemplate?.name ?? 'Report'}`}
        size="sm"
      >
        <Stack gap="md">
          {selectedTemplate?.description && (
            <Text size="sm" c="dimmed">{selectedTemplate.description}</Text>
          )}

          <Group gap="xs">
            <Badge>PDF</Badge>
            {selectedTemplate?.category && (
              <Badge variant="light" color={CATEGORY_COLORS[selectedTemplate.category] ?? 'gray'}>
                {selectedTemplate.category}
              </Badge>
            )}
          </Group>

          <Group grow>
            <Button
              leftSection={<IconDownload size={14} />}
              onClick={handleGenerate}
              loading={generating}
            >
              Download
            </Button>
            <Button
              variant="light"
              leftSection={<IconPrinter size={14} />}
              onClick={handleGenerate}
              loading={generating}
            >
              Print
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
